import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface PixTransactionData {
  qrCode: string;
  pixCode: string;
  expiresAt: string;
  transactionHash: string;
}

export function usePixTransaction(transactionHash: string | null) {
  const [status, setStatus] = useState<'pending' | 'paid' | 'expired' | 'canceled'>('pending');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const startTimeRef = useRef<number>(Date.now());
  const retryCountRef = useRef<number>(0);
  const MAX_RETRIES = 3;

  // Consultar apenas o banco (rápido, para webhook)
  const checkDatabase = useCallback(async () => {
    if (!transactionHash) return;

    try {
      const { data, error } = await supabase
        .from('tribopay_transactions')
        .select('status, expires_at, updated_at')
        .eq('tribopay_hash', transactionHash)
        .single();

      if (error) throw error;

      if (data) {
        setStatus(data.status as 'pending' | 'paid' | 'expired' | 'canceled');

        // Verificar se expirou
        if (data.expires_at && new Date(data.expires_at) < new Date() && data.status === 'pending') {
          setStatus('expired');
        }

        if (data.status === 'paid') {
          toast({
            title: '✅ Pagamento confirmado!',
            description: 'Seus créditos foram adicionados à sua conta.',
          });
        }
      }

      retryCountRef.current = 0; // Reset retry on success
    } catch (error) {
      console.error('Error checking database:', error);
      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current++;
        const delay = Math.pow(2, retryCountRef.current) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        await checkDatabase();
      }
    }
  }, [transactionHash, toast]);

  // Consultar via API (confiável, para quando webhook falha)
  const checkViaAPI = useCallback(async () => {
    if (!transactionHash) return;

    try {
      const { data, error } = await supabase.functions.invoke('check-tribopay-status', {
        body: { transactionHash }
      });

      if (error) {
        // Rate limit não é erro, apenas aguardar próximo intervalo
        if (error.status === 429 || error.message?.includes('Rate limit')) {
          console.log('[usePixTransaction] Rate limit, will retry on next interval');
          return;
        }
        // Apenas logar erros durante polling, não exibir toast
        console.log('[usePixTransaction] Polling error (will retry):', error.message);
        return;
      }

      if (data && data.success) {
        setStatus(data.status as 'pending' | 'paid' | 'expired' | 'canceled');

        if (data.status === 'paid') {
          toast({
            title: '✅ Pagamento confirmado!',
            description: 'Seus créditos foram adicionados à sua conta.',
          });
        }
      }

      retryCountRef.current = 0; // Reset retry on success
    } catch (error) {
      // Durante polling normal, apenas logar - não spammar usuário
      console.log('[usePixTransaction] Polling error:', error);
    }
  }, [transactionHash, toast]);

  const checkTransactionStatus = useCallback(async () => {
    if (!transactionHash) return;

    const elapsed = Date.now() - startTimeRef.current;
    const elapsedSeconds = Math.floor(elapsed / 1000);

    // Fase 1: 0-30s - Apenas consulta banco (webhook deve chegar)
    if (elapsedSeconds < 30) {
      await checkDatabase();
    } 
    // Fase 2 e 3: 30s+ - Consulta ativa via API
    else {
      await checkViaAPI();
    }
  }, [transactionHash, checkDatabase, checkViaAPI]);

  useEffect(() => {
    if (!transactionHash || status === 'paid' || status === 'expired' || status === 'canceled') {
      return;
    }

    // Reset start time quando transactionHash muda
    startTimeRef.current = Date.now();
    retryCountRef.current = 0;

    // Verificar status imediatamente
    checkTransactionStatus();

    // Função para determinar intervalo baseado no tempo decorrido
    const getInterval = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const elapsedSeconds = Math.floor(elapsed / 1000);

      if (elapsedSeconds < 30) return 3000;   // 3s - Fase 1: Webhook
      if (elapsedSeconds < 120) return 5000;  // 5s - Fase 2: API ativa
      if (elapsedSeconds < 300) return 10000; // 10s - Fase 3: API lenta

      // Timeout após 5 minutos
      setStatus('expired');
      return null;
    };

    let intervalId: NodeJS.Timeout;

    const setupInterval = () => {
      const interval = getInterval();
      if (interval === null) return;

      intervalId = setInterval(() => {
        checkTransactionStatus();
        // Reavaliar intervalo a cada check
        clearInterval(intervalId);
        setupInterval();
      }, interval);
    };

    setupInterval();

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [transactionHash, status, checkTransactionStatus]);

  return { status, loading, checkTransactionStatus };
}
